const DEFAULT_MIN_ANCHOR_LINE_LENGTH = 24;

export const DEFAULT_ANCHOR_COMPACTION_THRESHOLD = 128 * 1024;

export interface AnchorCompactionPlan {
  code: string;
  instructionSuffix: string;
  anchorCount: number;
  uniqueAnchorCount: number;
  hasStructuralValidation: boolean;
  expand: (content: string) => string;
}

export class FastApplyAnchorIntegrityError extends Error {
  readonly code = 'anchor_integrity_failed';
  readonly completionId?: string;

  constructor(completionId?: string) {
    super(
      'Fast Apply failed large-file anchor integrity checks. The partial result was discarded safely.'
    );
    this.name = 'FastApplyAnchorIntegrityError';
    this.completionId = completionId;
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function findNamespace(values: string[]): number {
  for (let namespace = 0; namespace < Number.MAX_SAFE_INTEGER; namespace++) {
    const prefix = `⟪M${namespace}:`;
    if (values.every((value) => !value.includes(prefix))) {
      return namespace;
    }
  }

  throw new Error('Unable to allocate a Fast Apply anchor namespace');
}

interface SourceUnit {
  value: string;
  label?: string;
  anchorable: boolean;
}

function htmlLabel(token: string): string {
  if (!token.startsWith('<')) return 'text';
  const match = token.match(/^<\s*(!doctype|\/?[a-zA-Z][\w:-]*)/);
  return (match?.[1] ?? 'tag').toLowerCase();
}

function htmlTokenUnits(value: string, visibleTags = new Set<string>()): SourceUnit[] {
  return (value.match(/<[^>]*>|[^<]+/g) ?? [value]).map((token) => ({
    value: token,
    label: htmlLabel(token),
    anchorable: token.length >= DEFAULT_MIN_ANCHOR_LINE_LENGTH
      && !visibleTags.has(htmlLabel(token).replace(/^\//, '')),
  }));
}

function sourceUnits(originalCode: string, editContext: string[]): SourceUnit[] {
  const trimmed = originalCode.trimStart().toLowerCase();
  const looksLikeHtml = (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html'))
    && /<\/html\s*>/i.test(originalCode);

  if (looksLikeHtml) {
    const contextWords = new Set(
      editContext.join(' ').toLowerCase().match(/[a-z][\w:-]*/g) ?? []
    );
    return htmlTokenUnits(originalCode, contextWords);
  }

  const parts = originalCode.split(/(\r\n|\n|\r)/);
  const units: SourceUnit[] = [];
  for (let index = 0; index < parts.length; index += 2) {
    const line = parts[index] ?? '';
    const lineEnding = parts[index + 1] ?? '';
    units.push({
      value: line,
      label: 'line',
      anchorable: line.length >= DEFAULT_MIN_ANCHOR_LINE_LENGTH,
    });
    if (lineEnding) {
      units.push({ value: lineEnding, anchorable: false });
    }
  }
  return units;
}

function copyCount(value: string): number | undefined {
  const words: Record<string, number> = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
  };
  return /^\d+$/.test(value) ? Number(value) : words[value.toLowerCase()];
}

function structuralExpectation(originalCode: string, editContext: string[]) {
  const context = editContext.join(' ');
  const match = context.match(
    /(?:for every|for each|each)\s+([a-z][\w:-]*)[\s\S]{0,120}?add\s+(one|two|three|four|five|six|seven|eight|nine|ten|\d+)\s+identical cop(?:y|ies)/i
  );
  if (!match) return undefined;

  const tag = match[1]!.toLowerCase();
  const addedCopies = copyCount(match[2]!);
  if (addedCopies === undefined) return undefined;

  const tagPattern = new RegExp(`<${escapeRegExp(tag)}(?:\\s|>)`, 'gi');
  const originalCount = originalCode.match(tagPattern)?.length ?? 0;
  if (originalCount === 0) return undefined;

  return { tag, expectedCount: originalCount * (addedCopies + 1) };
}

/**
 * Replace long source fragments with lossless placeholders. Definitions are sent once,
 * while the model can emit a small skeleton containing repeated/moved placeholders.
 */
export function createAnchorCompactionPlan(
  originalCode: string,
  collisionValues: string[] = []
): AnchorCompactionPlan | undefined {
  const namespace = findNamespace([originalCode, ...collisionValues]);
  const expectation = structuralExpectation(originalCode, collisionValues);
  const markerPrefix = `⟪M${namespace}:`;
  const definitions = new Map<string, number>();
  const values: string[] = [];
  const markers: string[] = [];
  let anchorCount = 0;
  let skeleton = '';

  for (const unit of sourceUnits(originalCode, collisionValues)) {
    if (!unit.anchorable) {
      skeleton += unit.value;
      continue;
    }

    let id = definitions.get(unit.value);
    if (id === undefined) {
      id = values.length;
      definitions.set(unit.value, id);
      values.push(unit.value);
      markers.push(`${markerPrefix}${id}:${unit.label ?? 'unit'}⟫`);
    }

    skeleton += markers[id];
    anchorCount++;
  }

  if (anchorCount === 0 || skeleton.length >= originalCode.length) {
    return undefined;
  }

  const definitionsBegin = `⟪MORPH_DEFINITIONS_BEGIN_${namespace}⟫`;
  const definitionsEnd = `⟪MORPH_DEFINITIONS_END_${namespace}⟫`;
  const codeBegin = `⟪MORPH_CODE_BEGIN_${namespace}⟫`;
  const serializedDefinitions = values
    .map((value, id) => `${markers[id]}=${JSON.stringify(value)}`)
    .join('\n');

  const code = [
    definitionsBegin,
    serializedDefinitions,
    definitionsEnd,
    codeBegin,
    skeleton,
  ].join('\n');

  const instructionSuffix = `

LARGE-FILE ANCHOR PROTOCOL:
The <code> block starts with an exact anchor definition table, then ${codeBegin}, then the source skeleton.
Each ${markerPrefix}N:kind⟫ placeholder represents the exact source fragment in its matching JSON-string definition. The kind suffix describes its HTML tag, text, or source line.
Return only the updated source skeleton after ${codeBegin}; never return the definition table or protocol delimiters.
Copy a placeholder exactly when its source fragment is unchanged. You may repeat, reorder, or remove placeholders when the requested edit requires it.
To modify an anchored fragment, output the modified source instead of its placeholder.
Do not invent or alter placeholder IDs.`;

  const markerPattern = new RegExp(`${escapeRegExp(markerPrefix)}(\\d+):[^⟫]+⟫`, 'g');

  return {
    code,
    instructionSuffix,
    anchorCount,
    uniqueAnchorCount: values.length,
    hasStructuralValidation: expectation !== undefined,
    expand(content: string): string {
      const codeBeginIndex = content.lastIndexOf(codeBegin);
      const skeletonContent = codeBeginIndex >= 0
        ? content.slice(codeBeginIndex + codeBegin.length).replace(/^\r?\n/, '')
        : content;

      const expanded = skeletonContent.replace(markerPattern, (marker, idText: string) => {
        const id = Number(idText);
        const value = values[id];
        return value === undefined || markers[id] !== marker ? marker : value;
      });

      if (expanded.includes(markerPrefix)) {
        throw new FastApplyAnchorIntegrityError();
      }

      if (expectation) {
        const tagPattern = new RegExp(`<${escapeRegExp(expectation.tag)}(?:\\s|>)`, 'gi');
        const actualCount = expanded.match(tagPattern)?.length ?? 0;
        if (actualCount !== expectation.expectedCount) {
          throw new FastApplyAnchorIntegrityError();
        }
      }

      return expanded;
    },
  };
}
